import {
  Fragment,
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
} from 'react'

import { useContentOverflow } from '../../../hooks'
import { getQaSelector, isEllipsis } from '../../../utils/common'
import { FloatingTooltip } from '../../Floating'
import { ICON_COLOR, IconWithTooltip } from '../../Icon'
import { Link } from '../../Link'
import { BreadcrumbsCollapsed } from '../BreadcrumbsCollapsed'

import {
  BREADCRUMB_MAX_CONTENT_WIDTH,
  BREADCRUMB_VARIANT,
  QA_NAME_BREADCRUMB,
  QA_NAME_BREADCRUMB_PAGE_TITLE,
} from './Breadcrumb.constants'
import { useBreadcrumbModClassName } from './Breadcrumb.hooks'
import {
  checkIsBreadCrumbTabVariant,
  formatItemsCount,
} from './Breadcrumb.utils'

import * as styles from './Breadcrumb.module.css'

import InfoIcon from '../../../icons/info.svg'

function Breadcrumb({
  active,
  checked,
  className = styles.Breadcrumb,
  clickable,
  count,
  head,
  hidden,
  items,
  option,
  variant = BREADCRUMB_VARIANT.DEFAULT,
  onHide,
}) {
  const isLinkedBreadcrumb = option.to

  const canClick = useMemo(
    () => !active || (clickable && !active) || (isLinkedBreadcrumb && !active),
    [active, clickable, head]
  )

  const { overflow, ref: textRef, setContentOverflowRef } = useContentOverflow()

  const isTabVariant = checkIsBreadCrumbTabVariant(variant)

  function checkOnHide() {
    if (
      checked &&
      !active &&
      (!isTabVariant || isEllipsis(textRef)) &&
      textRef.current?.offsetWidth < BREADCRUMB_MAX_CONTENT_WIDTH
    ) {
      onHide?.(option.id)
    }
  }

  useLayoutEffect(() => {
    checkOnHide()
  }, [checked, option])

  useEffect(() => {
    const ro = new ResizeObserver(() => {
      checkOnHide()
    })

    ro.observe(textRef.current)

    return () => {
      ro.disconnect()
    }
  }, [hidden, overflow])

  const hiddenItems = useMemo(() => {
    return items?.filter(i => hidden.has(i.id))
  }, [hidden, items])

  const handleClick = useCallback(
    e => {
      if (canClick) {
        option.onClick?.(e)
      }
    },
    [canClick, option.onClick]
  )

  const wrapperProps = isLinkedBreadcrumb
    ? { className: styles.link, to: option.to }
    : {}

  const WrapperComponent = isLinkedBreadcrumb ? Link : Fragment

  const breadCrumbClassName = useBreadcrumbModClassName({
    className,
    isActive: active && !head,
    isCheckedActive: checked,
    isHead: head,
    isLinkType: canClick,
    isTab: isTabVariant,
    isTabActive: isTabVariant && active,
  })

  const qaTitleProps = getQaSelector({
    value: head ? QA_NAME_BREADCRUMB_PAGE_TITLE : option.title,
  })

  const qaBreadCrumbProps = getQaSelector({
    value: QA_NAME_BREADCRUMB,
  })

  return (
    <div {...qaBreadCrumbProps} className={breadCrumbClassName}>
      <WrapperComponent {...wrapperProps}>
        <FloatingTooltip content={option.title} disabled={!overflow}>
          <span
            {...qaTitleProps}
            className={styles.title}
            ref={setContentOverflowRef}
            onClick={handleClick}
          >
            {option.title}
          </span>
        </FloatingTooltip>
      </WrapperComponent>
      {option?.info ? (
        <IconWithTooltip
          Icon={InfoIcon}
          color={ICON_COLOR.CurrentColor}
          tooltip={option.info}
        />
      ) : null}
      {active && Number.isFinite(count) ? (
        <span className={styles.counter}>{formatItemsCount(count)}</span>
      ) : null}
      {!active ? <span className={styles.slash}>/</span> : null}
      {head && hidden?.size > 0 ? (
        <>
          <BreadcrumbsCollapsed items={hiddenItems} />
          <span className={styles.slash}>/</span>
        </>
      ) : null}
    </div>
  )
}

export default Breadcrumb
